/**
 * Ported from `packages/ja-core/src/etymology.ts` in `git@git.fugl.dev:sks/yominoki.git`
 * @ `515ce239`. Bindery is canonical for this module from 2026-09-06; reconcile yominoki-side
 * drift toward this copy.
 */

// Per-kanji etymology providers + composer. Wiktionary is the primary
// source for glyph imagery and decomposition prose; Claude (wired via a
// callback in apps/web) fills any prose gaps. The composer lets the
// callsite plug in either alone or both together — a future Unihan
// loader drops in behind the same EtymologyProvider interface.
//
// Pure modules — no AI SDK import lives here. The Anthropic call is
// supplied as a callback by the consumer (same boundary rule as
// gloss.ts → translate / gloss callbacks).

export interface ScriptForm {
  url: string;
  attribution: string;
}

export interface ScriptForms {
  oracleBone?: ScriptForm;
  chuSlip?: ScriptForm;
  smallSeal?: ScriptForm;
}

export interface EtymologyData {
  decomposition?: string;
  semantic?: string;
  phonetic?: string;
  ancientForm?: string;
  onYomiOrigin?: string;
  gloss?: string;
  scriptForms?: ScriptForms;
  wiktionaryRevision?: number;
  /** Which provider(s) supplied this row. Set by the provider itself. */
  source: string;
  /** Exact model id when `source` includes `anthropic:*`. */
  model?: string;
}

export interface EtymologyProvider {
  describe(kanji: string): Promise<EtymologyData>;
}

// `\p{Script=Han}` covers BMP CJK Unified Ideographs, Extension A, and
// Extensions B–G in the supplementary planes. The `u` flag is required
// for `\p{}` to be interpreted as a Unicode property escape.
const KANJI_RE = /^\p{Script=Han}$/u;

/** Extract unique kanji codepoints from a display string, in order. */
export function extractKanji(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ch of text) {
    if (KANJI_RE.test(ch) && !seen.has(ch)) {
      seen.add(ch);
      out.push(ch);
    }
  }
  return out;
}

const WIKIMEDIA_USER_AGENT =
  "yominoki/0.1 (+https://yominoki.example.com; rf@sara.ai)";

const WIKIMEDIA_ATTRIBUTION = "Wikimedia Commons / CC BY-SA 4.0";

const SCRIPT_NAME_MATCHERS: {
  key: keyof ScriptForms;
  test: (s: string) => boolean;
}[] = [
  {
    key: "oracleBone",
    test: (s) => /oracle\s*bone/i.test(s),
  },
  {
    key: "chuSlip",
    test: (s) => /chu\s*slip|chu.*silk/i.test(s),
  },
  {
    key: "smallSeal",
    test: (s) => /small\s*seal/i.test(s),
  },
];

interface WiktionaryParseResponse {
  parse?: {
    text?: { "*"?: string };
    revid?: number;
  };
}

/**
 * Wiktionary parser via the MediaWiki Action API. Returns a partial
 * EtymologyData with `source: 'wiktionary'` and `wiktionaryRevision`
 * populated when the page is found. On 404 / parse failure / empty
 * extraction, returns `{ source: 'wiktionary' }` with no data so the
 * composer can fall back.
 */
export function createWiktionaryEtymologyProvider(opts?: {
  fetch?: typeof fetch;
}): EtymologyProvider {
  const doFetch = opts?.fetch ?? fetch;
  return {
    async describe(kanji: string): Promise<EtymologyData> {
      const url = new URL("https://en.wiktionary.org/w/api.php");
      url.searchParams.set("action", "parse");
      url.searchParams.set("page", kanji);
      url.searchParams.set("format", "json");
      url.searchParams.set("prop", "text|revid");
      url.searchParams.set("disabletoc", "1");
      url.searchParams.set("origin", "*");

      let body: WiktionaryParseResponse;
      try {
        const res = await doFetch(url.toString(), {
          headers: { "User-Agent": WIKIMEDIA_USER_AGENT },
        });
        if (!res.ok) return { source: "wiktionary" };
        body = (await res.json()) as WiktionaryParseResponse;
      } catch {
        return { source: "wiktionary" };
      }

      const html = body.parse?.text?.["*"];
      const revid = body.parse?.revid;
      if (!html) return { source: "wiktionary" };

      const data: EtymologyData = {
        source: "wiktionary",
        wiktionaryRevision: typeof revid === "number" ? revid : undefined,
      };

      const region = sliceGlyphOriginRegion(html);
      if (!region) {
        console.warn(
          `[etymology] Wiktionary page for ${kanji} has no Glyph origin section (revid=${revid})`,
        );
        return data;
      }

      const scriptForms = extractScriptForms(region);
      if (scriptForms) {
        data.scriptForms = scriptForms;
      } else {
        console.warn(
          `[etymology] Wiktionary page for ${kanji} has Glyph origin but no script-form imagery (revid=${revid})`,
        );
      }

      const prose = extractGlyphOriginProse(region);
      if (prose) {
        // Whether to land in `decomposition` vs `ancientForm` is a soft
        // call — the composer's Claude pass will fill the other slot if
        // we miss. Bias the first paragraph to `decomposition` if it
        // mentions a component-style decomposition.
        if (looksLikeDecomposition(prose)) {
          data.decomposition = prose;
        } else {
          data.ancientForm = prose;
        }
      }

      return data;
    },
  };
}

/**
 * Wraps a `describe` callback (typically calling the Anthropic SDK) as
 * an EtymologyProvider. apps/web supplies the callback so ja-core stays
 * AI-SDK-free.
 */
export function createClaudeEtymologyProvider(
  describe: (kanji: string) => Promise<Omit<EtymologyData, "source" | "model">>,
  model: string,
  sourceLabel = `anthropic:${model}`,
): EtymologyProvider {
  return {
    async describe(kanji: string): Promise<EtymologyData> {
      const data = await describe(kanji);
      return { ...data, source: sourceLabel, model };
    },
  };
}

/**
 * Compose two providers — primary (Wiktionary) first, fallback (Claude)
 * for any missing prose fields. The result's `source` reflects what
 * actually contributed: `'wiktionary'` if the primary covered everything,
 * `'composite'` if both ran, or the fallback's source verbatim if the
 * primary returned nothing.
 */
export function composeProviders(
  primary: EtymologyProvider,
  fallback: EtymologyProvider,
): EtymologyProvider {
  return {
    async describe(kanji: string): Promise<EtymologyData> {
      const a = await primary.describe(kanji);
      if (hasAllProse(a) || isPictographComplete(a)) return a;

      const aHasAnything = hasAnyContribution(a);
      const b = await fallback.describe(kanji);

      if (!aHasAnything) {
        // Primary returned nothing useful — fallback is the sole source.
        // Preserve fallback's source/model.
        return b;
      }

      return {
        decomposition: a.decomposition ?? b.decomposition,
        semantic: a.semantic ?? b.semantic,
        phonetic: a.phonetic ?? b.phonetic,
        ancientForm: a.ancientForm ?? b.ancientForm,
        onYomiOrigin: a.onYomiOrigin ?? b.onYomiOrigin,
        gloss: a.gloss ?? b.gloss,
        scriptForms: a.scriptForms ?? b.scriptForms,
        wiktionaryRevision: a.wiktionaryRevision,
        source: "composite",
        model: b.model,
      };
    },
  };
}

// ---------- internals ----------

const PROSE_FIELDS = [
  "decomposition",
  "semantic",
  "phonetic",
  "onYomiOrigin",
  "gloss",
] as const;

function hasAllProse(d: EtymologyData): boolean {
  return PROSE_FIELDS.every((k) => Boolean(d[k]));
}

/**
 * Pictographs (e.g. 大, 日, 月) have no phono-semantic split, so the
 * primary provider cannot supply `semantic` / `phonetic` — firing the
 * fallback to fill them is wasted budget. When the primary contributed
 * imagery + a non-component-marker description, treat the result as
 * complete-for-this-kanji-class and skip the fallback.
 */
function isPictographComplete(d: EtymologyData): boolean {
  const proseSlot = d.decomposition ?? d.ancientForm;
  if (!proseSlot) return false;
  if (looksLikeDecomposition(proseSlot)) return false;
  return Boolean(d.scriptForms && Object.keys(d.scriptForms).length > 0);
}

function hasAnyContribution(d: EtymologyData): boolean {
  if (d.scriptForms && Object.keys(d.scriptForms).length > 0) return true;
  for (const k of PROSE_FIELDS) {
    if (d[k]) return true;
  }
  if (d.ancientForm) return true;
  return false;
}

function looksLikeDecomposition(prose: string): boolean {
  // A decomposition usually mentions "+" or "component" or names
  // semantic/phonetic explicitly.
  return /[+＋]|component|semantic|phonetic/i.test(prose);
}

/**
 * Locate the first wikitable inside a pre-sliced Glyph origin region and
 * pull image URLs for the three target script forms. Best effort — the
 * Wiktionary HTML is stable but not formally specified.
 */
function extractScriptForms(region: string): ScriptForms | undefined {
  const tableMatch =
    /<table[^>]*class="[^"]*wikitable[^"]*"[^>]*>([\s\S]*?)<\/table>/i.exec(
      region,
    );
  if (!tableMatch) return undefined;
  const tableHtml = tableMatch[1]!;

  const headers = collectCellTextSequence(
    tableHtml,
    /<th\b[^>]*>([\s\S]*?)<\/th>/gi,
  );
  const cellHtmls = collectCellHtmls(tableHtml);
  if (headers.length === 0 || cellHtmls.length === 0) return undefined;

  const out: ScriptForms = {};
  const pairCount = Math.min(headers.length, cellHtmls.length);
  for (let i = 0; i < pairCount; i++) {
    const name = headers[i]!;
    const cell = cellHtmls[i]!;
    const match = SCRIPT_NAME_MATCHERS.find((m) => m.test(name));
    if (!match) continue;
    const url = extractFirstImgUrl(cell);
    if (!url) continue;
    out[match.key] = { url, attribution: WIKIMEDIA_ATTRIBUTION };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function sliceGlyphOriginRegion(html: string): string | null {
  // Find any `id="Glyph_origin*"` heading anchor — Wiktionary appends `_2`
  // / `_3` etc. when a page has multiple Glyph origin sections, and
  // sometimes uses just `Glyph_origin` for the Translingual section.
  const anchorRe = /id="Glyph_origin[^"]*"/i;
  const m = anchorRe.exec(html);
  if (!m) return null;
  const start = m.index;
  const tail = html.slice(start);
  // Skip the current heading's open tag — find the *next* one after a
  // small offset so we don't immediately bail at the same tag.
  const afterFirst = tail.indexOf(">", 0);
  if (afterFirst === -1) return tail;
  const followup = tail.slice(afterFirst).search(/<h[234]\b/i);
  if (followup === -1) return tail;
  return tail.slice(0, afterFirst + followup);
}

function collectCellTextSequence(html: string, re: RegExp): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  // RegExp with /g is reused — clone it to avoid sticky lastIndex bugs
  // when callers pass one in.
  const local = new RegExp(re.source, re.flags);
  while ((m = local.exec(html)) !== null) {
    out.push(stripTags(m[1]!).trim());
  }
  return out;
}

function collectCellHtmls(html: string): string[] {
  const out: string[] = [];
  const local = /<td\b[^>]*>([\s\S]*?)<\/td>/gi;
  let m: RegExpExecArray | null;
  while ((m = local.exec(html)) !== null) {
    out.push(m[1]!);
  }
  return out;
}

function extractFirstImgUrl(cellHtml: string): string | undefined {
  const m = /<img\b[^>]*\bsrc="([^"]+)"/i.exec(cellHtml);
  if (!m) return undefined;
  let url = m[1]!;
  if (url.startsWith("//")) url = "https:" + url;
  // Strip /thumb/.../NNNpx- to recover the original asset when the URL
  // points at a thumbnail. The shape is:
  //   /wikipedia/commons/thumb/a/ab/foo.png/120px-foo.png
  // becoming
  //   /wikipedia/commons/a/ab/foo.png
  const thumb =
    /^(.*?\/wikipedia\/[^/]+\/)thumb\/([^/]+\/[^/]+\/[^/]+)\/[^/]+$/.exec(url);
  if (thumb) {
    url = thumb[1]! + thumb[2]!;
  }
  return url;
}

function extractGlyphOriginProse(region: string): string | undefined {
  // Find the first <p> that isn't nested inside a <table>. Cheap heuristic:
  // remove all <table>...</table> blocks first, then search for the first <p>.
  const stripped = region.replace(/<table[\s\S]*?<\/table>/gi, "");
  const match = /<p\b[^>]*>([\s\S]*?)<\/p>/i.exec(stripped);
  if (!match) return undefined;
  const text = stripTags(match[1]!).trim();
  return text.length > 0 ? text : undefined;
}

function stripTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x([0-9a-fA-F]+);/g, (_: string, n: string) =>
      String.fromCodePoint(parseInt(n, 16)),
    )
    .replace(/&#(\d+);/g, (_: string, n: string) =>
      String.fromCodePoint(parseInt(n, 10)),
    )
    .replace(/\s+/g, " ");
}
